import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Tag, Switch, Tooltip } from 'antd';
import { 
    EditOutlined, 
    DeleteOutlined, 
    PlusOutlined, 
    EyeOutlined,
    GiftOutlined
} from '@ant-design/icons';
import VoucherApi from '../../api/voucher/voucher.api';
import { Voucher, CreateVoucherDto, UpdateVoucherDto, DiscountType } from '../../common/voucher.interface';
import CModalAddVoucher from '../../components/modal/CModalAddVoucher';
import CModalEditVoucher from '../../components/modal/CModalEditVoucher';
import CModalViewVoucher from '../../components/modal/CModalViewVoucher';
import { motion } from "framer-motion";
import { QUERY_PARAM } from '../../constants/get-api.constant';
import './voucher.styles.scss';

const VoucherPage: React.FC = () => {
    const [vouchers, setVouchers] = useState<Voucher[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalAddVisible, setIsModalAddVisible] = useState(false);
    const [isModalEditVisible, setIsModalEditVisible] = useState(false);
    const [isModalViewVisible, setIsModalViewVisible] = useState(false);
    const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
    const [currentSearchValue, setCurrentSearchValue] = useState<any>({
        size: QUERY_PARAM.size,
        offset: 0,
    });
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        fetchVouchers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchVouchers = async (searchParams = currentSearchValue) => {
        setLoading(true);
        try {
            const response = await VoucherApi.getAll().toPromise();
            if (response) {
                // Xử lý response theo API guide structure
                const voucherData = response.data || response;
                
                // Áp dụng phân trang thủ công vì API chưa hỗ trợ pagination
                const startIndex = searchParams.offset || 0;
                const pageSize = searchParams.size || QUERY_PARAM.size;
                const paginatedData = voucherData.slice(startIndex, startIndex + pageSize);
                
                setVouchers(paginatedData);
                setTotalRecords(voucherData.length);
            }
        } catch (error) {
            message.error('Không thể tải danh sách voucher');
        } finally {
            setLoading(false);
        }
    };

    const handleAddVoucher = async (data: CreateVoucherDto) => {
        try {
            const response = await VoucherApi.create(data).toPromise();
            if (response) {
                message.success('Tạo voucher thành công');
                setIsModalAddVisible(false);
                fetchVouchers();
            }
        } catch (error) {
            message.error('Không thể tạo voucher');
        }
    };

    const handleEditVoucher = async (data: UpdateVoucherDto) => {
        if (!selectedVoucher) return;

        try {
            const response = await VoucherApi.update(selectedVoucher.id, data).toPromise();
            if (response) {
                message.success('Cập nhật voucher thành công');
                setIsModalEditVisible(false);
                setSelectedVoucher(null);
                fetchVouchers();
            }
        } catch (error) {
            message.error('Không thể cập nhật voucher');
        }
    };

    const handleDeleteVoucher = async (id: number) => {
        try {
            await VoucherApi.delete(id).toPromise();
            message.success('Xóa voucher thành công');
            fetchVouchers();
        } catch (error) {
            message.error('Không thể xóa voucher');
        }
    };

    const handleToggleStatus = async (voucher: Voucher) => {
        try {
            const response = await VoucherApi.update(voucher.id, {
                isActive: !voucher.isActive
            }).toPromise();
            if (response) {
                message.success('Cập nhật trạng thái thành công');
                fetchVouchers();
            }
        } catch (error) {
            message.error('Không thể cập nhật trạng thái');
        }
    };

    const handleViewVoucher = (voucher: Voucher) => {
        setSelectedVoucher(voucher);
        setIsModalViewVisible(true);
    };

    const handleEditVoucherClick = (voucher: Voucher) => {
        setSelectedVoucher(voucher);
        setIsModalEditVisible(true);
    };

    // Helper function để map dữ liệu sang Backend Schema chính xác
    const mapVoucherData = (voucher: any): CreateVoucherDto => {
        const mappedData: CreateVoucherDto = {
            // Required fields
            code: voucher.code,
            name: voucher.title,                               // title -> name
            type: voucher.pointsRequired > 0 ? 'PAID' : 'FREE',
            discount: voucher.discountValue,                   // discountValue -> discount
            quantity: voucher.usageLimit,                      // usageLimit -> quantity
            startDate: voucher.startDate,
            endDate: voucher.endDate,
            
            // Optional fields
            description: voucher.description,
            discountType: voucher.discountType,
            minOrder: voucher.minOrderValue,                   // minOrderValue -> minOrder
            pointsCost: voucher.pointsRequired,                // pointsRequired -> pointsCost
            isActive: voucher.isActive,
            
            // Frontend compatibility
            title: voucher.title,
            discountValue: voucher.discountValue,
            minOrderValue: voucher.minOrderValue,
            pointsRequired: voucher.pointsRequired,
            usageLimit: voucher.usageLimit,
            usagePerUser: voucher.usagePerUser,
        };

        // Chỉ thêm maxDiscount khi discountType là PERCENTAGE
        if (voucher.discountType === DiscountType.PERCENTAGE && voucher.maxDiscountAmount) {
            mappedData.maxDiscount = voucher.maxDiscountAmount;
            mappedData.maxDiscountAmount = voucher.maxDiscountAmount;
        }

        return mappedData;
    };

    const onChangePagination = (page: number, pageSize?: number) => {
        const newSearchValue = {
            ...currentSearchValue,
            offset: (page - 1) * (pageSize || QUERY_PARAM.size),
            size: pageSize || QUERY_PARAM.size
        };
        setCurrentSearchValue(newSearchValue);
        fetchVouchers(newSearchValue);
    };

    const createSampleVouchers = async () => {
        const sampleVouchers = [
            // Free vouchers (không tốn điểm)
            {
                code: 'WELCOME10',
                title: 'Chào mừng thành viên mới',
                description: 'Giảm 10% cho đơn hàng đầu tiên',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 10,
                minOrderValue: 100000,
                maxDiscountAmount: 50000,
                pointsRequired: 0,
                usageLimit: 100,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 ngày
                isActive: true
            },
            {
                code: 'FREESHIP50',
                title: 'Miễn phí vận chuyển',
                description: 'Giảm 50k phí vận chuyển',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 50000,
                minOrderValue: 200000,
                maxDiscountAmount: 50000,
                pointsRequired: 0,
                usageLimit: 200,
                usagePerUser: 2,
                startDate: new Date(),
                endDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'SUMMER15',
                title: 'Ưu đãi mùa hè',
                description: 'Giảm 15% cho tất cả sản phẩm',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 15,
                minOrderValue: 150000,
                maxDiscountAmount: 100000,
                pointsRequired: 0,
                usageLimit: 300,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'STUDENT20',
                title: 'Ưu đãi sinh viên',
                description: 'Giảm 20% dành cho sinh viên',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 20,
                minOrderValue: 100000,
                maxDiscountAmount: 80000,
                pointsRequired: 0,
                usageLimit: 150,
                usagePerUser: 3,
                startDate: new Date(),
                endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'NEWBIE100',
                title: 'Voucher người dùng mới',
                description: 'Giảm 100k cho thành viên mới',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 100000,
                minOrderValue: 300000,
                maxDiscountAmount: 100000,
                pointsRequired: 0,
                usageLimit: 50,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            // Vouchers trả phí bằng điểm
            {
                code: 'VIP25',
                title: 'Voucher VIP 25%',
                description: 'Giảm 25% cho thành viên VIP',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 25,
                minOrderValue: 200000,
                maxDiscountAmount: 200000,
                pointsRequired: 100,
                usageLimit: 50,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'PLATINUM150',
                title: 'Voucher Platinum',
                description: 'Giảm 150k cho thành viên Platinum',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 150000,
                minOrderValue: 500000,
                maxDiscountAmount: 150000,
                pointsRequired: 200,
                usageLimit: 30,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'MEGA30',
                title: 'Mega Sale 30%',
                description: 'Giảm 30% cho đơn hàng lớn',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 30,
                minOrderValue: 400000,
                maxDiscountAmount: 300000,
                pointsRequired: 150,
                usageLimit: 40,
                usagePerUser: 2,
                startDate: new Date(),
                endDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'GOLD200',
                title: 'Voucher Gold 200k',
                description: 'Giảm 200k cho thành viên Gold',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 200000,
                minOrderValue: 600000,
                maxDiscountAmount: 200000,
                pointsRequired: 250,
                usageLimit: 25,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'PREMIUM35',
                title: 'Premium Discount',
                description: 'Giảm 35% cho thành viên Premium',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 35,
                minOrderValue: 500000,
                maxDiscountAmount: 400000,
                pointsRequired: 300,
                usageLimit: 20,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'FLASH250',
                title: 'Flash Sale 250k',
                description: 'Giảm 250k trong thời gian giới hạn',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 250000,
                minOrderValue: 700000,
                maxDiscountAmount: 250000,
                pointsRequired: 350,
                usageLimit: 15,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'LOYALTY40',
                title: 'Ưu đãi khách hàng thân thiết',
                description: 'Giảm 40% cho khách hàng trung thành',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 40,
                minOrderValue: 600000,
                maxDiscountAmount: 500000,
                pointsRequired: 400,
                usageLimit: 10,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'DIAMOND300',
                title: 'Diamond Member',
                description: 'Giảm 300k cho thành viên Diamond',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 300000,
                minOrderValue: 800000,
                maxDiscountAmount: 300000,
                pointsRequired: 500,
                usageLimit: 8,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'ULTIMATE50',
                title: 'Ultimate Voucher',
                description: 'Giảm 50% tối đa cho đơn hàng lớn',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 50,
                minOrderValue: 1000000,
                maxDiscountAmount: 600000,
                pointsRequired: 600,
                usageLimit: 5,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'EXCLUSIVE400',
                title: 'Exclusive Deal',
                description: 'Giảm 400k cho đơn hàng đặc biệt',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 400000,
                minOrderValue: 1200000,
                maxDiscountAmount: 400000,
                pointsRequired: 700,
                usageLimit: 3,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'BIRTHDAY25',
                title: 'Ưu đãi sinh nhật',
                description: 'Giảm 25% nhân dịp sinh nhật',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 25,
                minOrderValue: 250000,
                maxDiscountAmount: 150000,
                pointsRequired: 80,
                usageLimit: 60,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'WEEKEND120',
                title: 'Weekend Special',
                description: 'Giảm 120k cho cuối tuần',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 120000,
                minOrderValue: 400000,
                maxDiscountAmount: 120000,
                pointsRequired: 120,
                usageLimit: 70,
                usagePerUser: 2,
                startDate: new Date(),
                endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'COMBO22',
                title: 'Combo Deal 22%',
                description: 'Giảm 22% khi mua combo',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 22,
                minOrderValue: 300000,
                maxDiscountAmount: 180000,
                pointsRequired: 90,
                usageLimit: 80,
                usagePerUser: 2,
                startDate: new Date(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'HOLIDAY180',
                title: 'Holiday Special',
                description: 'Giảm 180k cho ngày lễ',
                discountType: DiscountType.FIXED_AMOUNT,
                discountValue: 180000,
                minOrderValue: 550000,
                maxDiscountAmount: 180000,
                pointsRequired: 180,
                usageLimit: 45,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
                isActive: true
            },
            {
                code: 'BUNDLE28',
                title: 'Bundle Discount',
                description: 'Giảm 28% khi mua bundle',
                discountType: DiscountType.PERCENTAGE,
                discountValue: 28,
                minOrderValue: 450000,
                maxDiscountAmount: 250000,
                pointsRequired: 160,
                usageLimit: 35,
                usagePerUser: 1,
                startDate: new Date(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                isActive: true
            }
        ];

        try {
            setLoading(true);
            let successCount = 0;
            let errorCount = 0;

            for (let i = 0; i < sampleVouchers.length; i++) {
                try {
                    const mappedVoucher = mapVoucherData(sampleVouchers[i]);
                    await VoucherApi.create(mappedVoucher).toPromise();
                    successCount++;
                    // Delay 200ms giữa các request để tránh spam
                    await new Promise(resolve => setTimeout(resolve, 200));
                } catch (error) {
                    errorCount++;
                    console.error(`Lỗi tạo voucher ${sampleVouchers[i].code}:`, error);
                }
            }

            if (successCount > 0) {
                message.success(`Đã tạo thành công ${successCount} voucher mẫu!`);
                fetchVouchers();
            }
            
            if (errorCount > 0) {
                message.warning(`${errorCount} voucher không thể tạo (có thể đã tồn tại)`);
            }
        } catch (error) {
            message.error('Có lỗi xảy ra khi tạo voucher mẫu');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'STT',
            key: 'stt',
            width: 40,
            render: (_: any, __: any, index: number) => (
                <div style={{ textAlign: "center" }}>
                    {currentSearchValue.offset + index + 1}
                </div>
            ),
        },
        {
            title: 'Mã voucher',
            dataIndex: 'code',
            key: 'code',
            width: 90,
            render: (code: string) => (
                <div style={{ textAlign: "center" }}>
                    {code}
                </div>
            ),
        },
        {
            title: 'Tên voucher',
            dataIndex: 'name',
            key: 'name',
            width: 140,
            render: (name: string, record: Voucher) => (
                <div style={{ textAlign: "center" }}>
                    {name || record.title || 'N/A'}
                </div>
            ),
        },
        {
            title: 'Loại giảm giá',
            dataIndex: 'discountType',
            key: 'discountType',
            width: 120,
            render: (discountType: DiscountType, record: Voucher) => (
                <div style={{ textAlign: "center" }}>
                    <Tag color={discountType === DiscountType.PERCENTAGE ? 'blue' : 'green'}>
                        {discountType === DiscountType.PERCENTAGE ? 'Phần trăm' : 'Số tiền cố định'}
                    </Tag>
                </div>
            ),
        },
        {
            title: 'Giá trị giảm',
            dataIndex: 'discount',
            key: 'discount',
            width: 120,
            render: (discount: number, record: Voucher) => {
                const value = discount || record.discountValue || 0;
                const type = record.discountType || DiscountType.PERCENTAGE;
                return (
                    <div style={{ textAlign: "center" }}>
                        {type === DiscountType.PERCENTAGE ? `${value}%` : `${value.toLocaleString()} VND`}
                    </div>
                );
            },
        },
        {
            title: 'Giới hạn sử dụng',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 120,
            render: (quantity: number, record: Voucher) => (
                <div style={{ textAlign: "center" }}>
                    {quantity || record.usageLimit || 'Không giới hạn'}
                </div>
            ),
        },
        {
            title: 'Còn lại',
            key: 'remaining',
            width: 100,
            render: (record: Voucher) => {
                const limit = record.quantity || record.usageLimit || 0;
                const used = record.usedCount || 0;
                const remaining = limit > 0 ? limit - used : 'Không giới hạn';
                return (
                    <div style={{ textAlign: "center" }}>
                        {remaining}
                    </div>
                );
            },
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (isActive: boolean, record: Voucher) => (
                <div style={{ textAlign: "center" }}>
                    <Switch
                        checked={isActive}
                        onChange={() => handleToggleStatus(record)}
                        checkedChildren="Hoạt động"
                        unCheckedChildren="Tạm dừng"
                    />
                </div>
            ),
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 100,
            render: (record: Voucher) => (
                <Space size="small" style={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Xem chi tiết">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewVoucher(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Chỉnh sửa">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditVoucherClick(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Xóa">
                        <Popconfirm
                            title="Bạn có chắc chắn muốn xóa voucher này?"
                            onConfirm={() => handleDeleteVoucher(record.id)}
                            okText="Có"
                            cancelText="Không"
                        >
                            <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                style={{ color: '#ff4d4f' }}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <motion.div
            className="sketch-main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <div className="table-area">
                <div className="voucher-header">
                    <div className="voucher-title">
                        <Space>
                            <GiftOutlined style={{ fontSize: '20px', color: 'var(--primary-color)' }} />
                            <h1>Quản lý Voucher</h1>
                        </Space>
                    </div>
                    <Space>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIsModalAddVisible(true)}
                            className="add-voucher-btn"
                        >
                            Thêm voucher mới
                        </Button>
                        <Button
                            type="default"
                            icon={<GiftOutlined />}
                            onClick={createSampleVouchers}
                            className="sample-vouchers-btn"
                            loading={loading}
                        >
                            Tạo 20 voucher mẫu
                        </Button>
                    </Space>
                </div>

                <div className="voucher-content">
                    <Table
                        columns={columns}
                        dataSource={vouchers}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            current: Math.floor(currentSearchValue.offset / currentSearchValue.size) + 1,
                            pageSize: currentSearchValue.size,
                            total: totalRecords,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} của ${total} voucher`,
                            onChange: onChangePagination,
                            onShowSizeChange: onChangePagination,
                        }}
                        scroll={{ x: 1000, y: 500 }}
                        style={{ minWidth: '1000px' }}
                        className="voucher-table"
                    />
                </div>
            </div>

            <CModalAddVoucher
                visible={isModalAddVisible}
                onCancel={() => setIsModalAddVisible(false)}
                onOk={handleAddVoucher}
            />

            <CModalEditVoucher
                visible={isModalEditVisible}
                voucher={selectedVoucher}
                onCancel={() => {
                    setIsModalEditVisible(false);
                    setSelectedVoucher(null);
                }}
                onOk={handleEditVoucher}
            />

            <CModalViewVoucher
                visible={isModalViewVisible}
                voucher={selectedVoucher}
                onCancel={() => {
                    setIsModalViewVisible(false);
                    setSelectedVoucher(null);
                }}
            />
        </motion.div>
    );
};

export default VoucherPage; 